/**
 * Admin Resource
 *
 * Thin 1:1 mapping to the admin API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  AuditLog,
  ListAdminActionsParams,
  GetEntityAuditTrailParams,
  UsageSummary,
  GetPlatformUsageParams,
  GetAccountUsageParams,
  ExportUsageParams,
  AdminSession,
  ListSessionsAdminParams,
  ReplaySessionRequest,
  ReplaySessionResponse,
  ActiveAgent,
  ListActiveAgentsParams,
  TerminateAgentResponse,
  BridgeConnection,
  SystemPersonaWithConfig,
} from '../types/index.js';

export class AdminResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/admin';

  // --- Audit Logs ---

  async listAdminActions(
    params?: ListAdminActionsParams,
  ): Promise<AuditLog[]> {
    return this.http.get(`${this.prefix}/audit/logs`, params);
  }

  async getEntityAuditTrail(
    entityType: string,
    entityId: string,
    params?: GetEntityAuditTrailParams,
  ): Promise<AuditLog[]> {
    return this.http.get(
      `${this.prefix}/audit/entity/${entityType}/${entityId}`,
      params,
    );
  }

  // --- Usage Analytics ---

  async getPlatformUsageSummary(
    params?: GetPlatformUsageParams,
  ): Promise<UsageSummary> {
    return this.http.get(`${this.prefix}/usage/summary`, params);
  }

  async getAccountUsageDetails(
    accountId: string,
    params: GetAccountUsageParams,
  ): Promise<UsageSummary> {
    return this.http.get(
      `${this.prefix}/usage/account/${accountId}`,
      params,
    );
  }

  async exportUsageData(params: ExportUsageParams): Promise<unknown> {
    return this.http.get(`${this.prefix}/usage/export`, params);
  }

  // --- Sessions Admin ---

  async listSessions(
    params?: ListSessionsAdminParams,
  ): Promise<AdminSession[]> {
    return this.http.get(`${this.prefix}/sessions`, params);
  }

  async getSession(sessionId: string): Promise<AdminSession> {
    return this.http.get(`${this.prefix}/sessions/${sessionId}`);
  }

  async replaySession(
    sessionId: string,
    params: ReplaySessionRequest,
  ): Promise<ReplaySessionResponse> {
    return this.http.post(
      `${this.prefix}/sessions/${sessionId}/replay`,
      params,
    );
  }

  // --- Agents Admin ---

  async listActiveAgents(
    params?: ListActiveAgentsParams,
  ): Promise<ActiveAgent[]> {
    return this.http.get(`${this.prefix}/agents`, params);
  }

  async terminateAgent(
    workflowId: string,
    reason: string,
  ): Promise<TerminateAgentResponse> {
    return this.http.post(
      `${this.prefix}/agents/${workflowId}/terminate?reason=${encodeURIComponent(reason)}`,
    );
  }

  // --- Bridge Admin ---

  async listBridgeConnections(): Promise<BridgeConnection[]> {
    return this.http.get(
      `${this.prefix}/infrastructure/bridge/connections`,
    );
  }

  // --- System Personas ---

  /** Admin catalog, including reserved `_system_*` cognitive personas. */
  async listSystemPersonas(): Promise<SystemPersonaWithConfig[]> {
    return this.http.post(`${this.prefix}/system-personas/list`, {});
  }
}
